import { graphql } from 'react-relay';
import { createAri } from '@atlassian/jira-platform-ari';
import type { resolutionQuery as resolutionQueryType } from '@atlassian/jira-relay/src/__generated__/resolutionQuery.graphql';

import { FieldValueLookupError } from '../../../common/errors';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';
import { isNotEmptyString } from '../../../common/utils/is-not-empty-string';

type ResolutionPublicGetValue = null | {
	id: string;
	value: string;
};

export type ResolutionPublicSetValue = null | string;

type ResolutionInternalValue =
	| {
			value: string;
			label: string;
	  }
	| null
	| undefined;

// we don't add validator in config for value field till we add support for setValue,
// if we add validator it will run publicToInternalTransformer,
// and if there is no such transformer it will pass income value to the component
// for more details see codebase
// src/packages/issue-adjustments/issue-adjustments/src/controllers/issue-adjustments/utils/supported-field-properties/index.tsx line 174
export const resolutionSetValueValidator = (value: unknown): value is ResolutionPublicSetValue =>
	value === null || isNotEmptyString(value);

export const resolutionGetValueTransformer = (
	internalValue: ResolutionInternalValue,
): ResolutionPublicGetValue => {
	if (!internalValue) {
		return null;
	}

	const { label, value } = internalValue;

	return {
		id: getResourceIdfromAri(value),
		value: label,
	};
};

// eslint-disable-next-line @atlassian/relay/must-use-inline
export const resolutionQuery = graphql`
	query resolutionQuery($ari: ID!, $filterById: JiraFieldOptionIdsFilterInput) {
		node(id: $ari) {
			... on JiraResolutionField {
				# eslint-disable-next-line @atlassian/relay/unused-fields
				resolutions(filterById: $filterById) {
					edges {
						node {
							value: id
							label: name
						}
					}
				}
			}
		}
	}
`;

export const resolutionSetValueTransformer = async (
	inputValue: ResolutionPublicSetValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<ResolutionInternalValue> => {
	if (!inputValue) return null;

	const resolutionARI = createAri({
		resourceOwner: 'jira',
		cloudId: tenantContext.cloudId,
		resourceType: 'resolution',
		activationId: tenantContext.activationId,
		resourceId: inputValue,
	});

	const response: {
		edges?: [
			{
				node?: {
					label?: string | null;
					value: string;
				};
			},
		];
	} = await fetchFieldOptions<resolutionQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs: [resolutionARI],
		nodePropertyName: 'resolutions',
		queryName: 'FETCH_RESOLUTION',
		query: resolutionQuery,
	});

	const resolution = response?.edges?.[0]?.node;

	if (resolution?.value !== resolutionARI) {
		throw new FieldValueLookupError(inputValue);
	}

	return {
		value: resolution.value,
		label: resolution.label ?? '',
	};
};
